<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { DxButton } from 'devextreme-vue/button';
import { DxCheckBox } from 'devextreme-vue/check-box';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxTextBox } from 'devextreme-vue/text-box';
import { showError, showSuccess } from '@botto/core';
import { geoApi, type GeoCredentials, type GeoStatus } from '../../api';

const status = ref<GeoStatus | null>(null);
const loading = ref(false);
const reloading = ref(false);
const downloading = ref(false);
const uploading = ref(false);
const savingCreds = ref(false);
const downloadLog = ref('');
const uploadName = ref('GeoLite2-City.mmdb');
const uploadFile = ref<File | null>(null);

const creds = reactive<GeoCredentials>({
  MAXMIND_LICENSE_KEY: '',
  IP2LOCATION_TOKEN: '',
  IP2PROXY_TOKEN: '',
});

const dl = reactive({
  maxmind: true,
  ip2location: false,
  ip2proxy: false,
});

const allowedNames = [
  'GeoLite2-City.mmdb',
  'GeoLite2-ASN.mmdb',
  'IP2LOCATION-LITE-DB11.BIN',
  'IP2LOCATION-LITE-ASN.BIN',
  'IP2PROXY-LITE-PX12.BIN',
];

const files = computed(() => status.value?.files || []);
const anyDownload = computed(() => dl.maxmind || dl.ip2location || dl.ip2proxy);
const busy = computed(
  () =>
    loading.value ||
    reloading.value ||
    downloading.value ||
    uploading.value ||
    savingCreds.value,
);

function formatBytes(n: number) {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / (1024 * 1024)).toFixed(1)} MB`;
}

function formatMtime(ts: number | null) {
  if (ts == null) return '—';
  try {
    return new Date(Number(ts) * 1000).toLocaleString();
  } catch {
    return String(ts);
  }
}

async function load() {
  loading.value = true;
  try {
    const [st, c] = await Promise.all([geoApi.geoStatus(), geoApi.geoCredentialsGet()]);
    status.value = st;
    creds.MAXMIND_LICENSE_KEY = c.MAXMIND_LICENSE_KEY || '';
    creds.IP2LOCATION_TOKEN = c.IP2LOCATION_TOKEN || '';
    creds.IP2PROXY_TOKEN = c.IP2PROXY_TOKEN || '';
  } catch (e) {
    showError(e);
    status.value = null;
  } finally {
    loading.value = false;
  }
}

async function onSaveCreds() {
  savingCreds.value = true;
  try {
    const c = await geoApi.geoCredentialsPut({ ...creds });
    creds.MAXMIND_LICENSE_KEY = c.MAXMIND_LICENSE_KEY || '';
    creds.IP2LOCATION_TOKEN = c.IP2LOCATION_TOKEN || '';
    creds.IP2PROXY_TOKEN = c.IP2PROXY_TOKEN || '';
    showSuccess('Токены сохранены');
  } catch (e) {
    showError(e);
  } finally {
    savingCreds.value = false;
  }
}

async function onReload() {
  reloading.value = true;
  try {
    await geoApi.geoReload();
    showSuccess('GEO readers перезагружены');
    await load();
  } catch (e) {
    showError(e);
  } finally {
    reloading.value = false;
  }
}

async function onDownload() {
  downloading.value = true;
  downloadLog.value = '';
  try {
    const result = await geoApi.geoDownload({
      maxmind: dl.maxmind,
      ip2location: dl.ip2location,
      ip2proxy: dl.ip2proxy,
    });
    downloadLog.value = JSON.stringify(result, null, 2);
    const errs = result?.errors || [];
    if (errs.length && !(result?.downloaded || []).length) {
      showError('Скачивание не удалось');
    } else if (errs.length) {
      showSuccess('Скачано с ошибками — см. лог');
    } else {
      showSuccess('Скачивание завершено');
    }
    await load();
  } catch (e) {
    showError(e);
    downloadLog.value = e instanceof Error ? e.message : String(e);
  } finally {
    downloading.value = false;
  }
}

function onFilePick(ev: Event) {
  const input = ev.target as HTMLInputElement;
  const f = input.files?.[0] || null;
  uploadFile.value = f;
  if (f?.name && allowedNames.includes(f.name)) {
    uploadName.value = f.name;
  }
}

async function onUpload() {
  if (!uploadFile.value) return;
  uploading.value = true;
  try {
    await geoApi.geoUpload(uploadName.value, uploadFile.value);
    showSuccess(`Загружено: ${uploadName.value}`);
    uploadFile.value = null;
    await load();
  } catch (e) {
    showError(e);
  } finally {
    uploading.value = false;
  }
}

onMounted(load);
</script>

<template>
  <div class="geo-db">
    <div class="geo-db__toolbar">
      <DxButton text="Обновить статус" styling-mode="outlined" :disabled="busy" @click="load" />
      <DxButton
        :text="reloading ? 'Перезагрузка…' : 'Reload readers'"
        styling-mode="outlined"
        :disabled="busy"
        @click="onReload"
      />
    </div>

    <p v-if="status?.geo_dir" class="muted">
      Каталог: <code>{{ status.geo_dir }}</code>
    </p>

    <table v-if="files.length" class="geo-db__table">
      <thead>
        <tr>
          <th>Файл</th>
          <th>На диске</th>
          <th>Размер</th>
          <th>Изменён</th>
          <th>В памяти</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="f in files" :key="f.filename">
          <td class="mono">{{ f.filename }}</td>
          <td>{{ f.exists ? 'да' : 'нет' }}</td>
          <td>{{ f.exists ? formatBytes(f.size) : '—' }}</td>
          <td>{{ f.exists ? formatMtime(f.mtime) : '—' }}</td>
          <td>
            <span :class="f.loaded ? 'ok' : 'bad'">{{ f.loaded ? 'да' : 'нет' }}</span>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else class="muted">{{ loading ? 'Загрузка…' : 'Нет данных.' }}</div>

    <section class="geo-db__card">
      <h3>Токены для скачивания</h3>
      <p class="muted hint">
        Хранятся в Postgres <code>app_settings</code> (ключ / значение). Непустое значение из БД
        перекрывает env.
      </p>
      <div class="field field--wide">
        <label>MAXMIND_LICENSE_KEY</label>
        <DxTextBox
          v-model:value="creds.MAXMIND_LICENSE_KEY"
          mode="password"
          :show-clear-button="true"
        />
      </div>
      <div class="field field--wide">
        <label>IP2LOCATION_TOKEN</label>
        <DxTextBox
          v-model:value="creds.IP2LOCATION_TOKEN"
          mode="password"
          :show-clear-button="true"
        />
      </div>
      <div class="field field--wide">
        <label>IP2PROXY_TOKEN</label>
        <DxTextBox
          v-model:value="creds.IP2PROXY_TOKEN"
          mode="password"
          :show-clear-button="true"
        />
      </div>
      <DxButton
        :text="savingCreds ? 'Сохранение…' : 'Сохранить токены'"
        type="default"
        :disabled="busy"
        @click="onSaveCreds"
      />
    </section>

    <section class="geo-db__card">
      <h3>Скачать у провайдера</h3>
      <p class="muted hint">Используются сохранённые токены выше (или env, если в БД пусто).</p>
      <div class="checks">
        <DxCheckBox v-model:value="dl.maxmind" text="MaxMind GeoLite2 (City + ASN)" />
        <DxCheckBox v-model:value="dl.ip2location" text="IP2Location LITE (DB11 + ASN)" />
        <DxCheckBox v-model:value="dl.ip2proxy" text="IP2Proxy LITE (PX12)" />
      </div>
      <DxButton
        :text="downloading ? 'Скачивание…' : 'Скачать и reload'"
        type="default"
        :disabled="busy || !anyDownload"
        @click="onDownload"
      />
      <pre v-if="downloadLog" class="log mono">{{ downloadLog }}</pre>
    </section>

    <section class="geo-db__card">
      <h3>Загрузить файл</h3>
      <div class="field">
        <label>Целевое имя</label>
        <DxSelectBox :items="allowedNames" v-model:value="uploadName" />
      </div>
      <div class="field">
        <label>Файл</label>
        <input type="file" @change="onFilePick" />
      </div>
      <DxButton
        :text="uploading ? 'Загрузка…' : 'Загрузить и reload'"
        type="default"
        :disabled="busy || !uploadFile || !uploadName"
        @click="onUpload"
      />
    </section>
  </div>
</template>

<style scoped>
.geo-db__toolbar {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
  flex-wrap: wrap;
}

.geo-db__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;
  margin-bottom: 1rem;
}

.geo-db__table th,
.geo-db__table td {
  text-align: left;
  padding: 0.5rem 0.4rem;
  border-bottom: 1px solid #e4e9f0;
}

.geo-db__card {
  border: 1px solid #e4e9f0;
  border-radius: 8px;
  padding: 0.9rem 1rem;
  margin-bottom: 1rem;
  background: #fff;
}

.geo-db__card h3 {
  margin: 0 0 0.5rem;
  font-size: 1rem;
}

.checks {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin: 0.65rem 0 0.85rem;
}

.field {
  margin-bottom: 0.75rem;
  max-width: 420px;
}

.field--wide {
  max-width: 560px;
}

.field label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.85rem;
  color: #475569;
}

.ok {
  color: #15803d;
  font-weight: 600;
}

.bad {
  color: #b91c1c;
}

.muted {
  color: #64748b;
}

.hint {
  margin: 0;
  font-size: 0.85rem;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.85rem;
}

.log {
  margin: 0.85rem 0 0;
  padding: 0.65rem 0.75rem;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 6px;
  font-size: 0.78rem;
  overflow: auto;
  max-height: 220px;
}
</style>
